import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import type { MarketplaceLead } from '@gcv/shared';
import { Box } from '@mui/material';

import { useComponent } from 'hooks';
import { useConvertBankMarketplaceLead } from 'hooks/mutations';
import { useBankTemplates } from 'hooks/queries';
import { getFiRelationshipManagerStore } from 'stores/FiRelationshipManagerStore';
import { getSnackbarStore } from 'stores/SnackBarStore';
import type { SelectOption } from 'ui';
import { Button, Dialog, InputSelect, Text } from 'ui';
import { getRelationshipManagerOptions } from '../utils/getRelationshipManagerOptions';

export const ConvertLeadModal: React.FC<{
  lead: MarketplaceLead;
  isOpen: boolean;
  onClose: () => void;
}> = useComponent((props) => {
  const navigate = useNavigate();
  const { lead, isOpen, onClose } = props;

  const relationshipManagerStore = getFiRelationshipManagerStore();
  const { onboardingWithoutArchived } = useBankTemplates();
  const { convertBankMarketplaceLead } = useConvertBankMarketplaceLead();
  const [isConverting, setIsConverting] = useState<boolean>(false);

  const form = useForm({
    mode: 'onBlur'
  });

  const templateOptions: SelectOption[] = Object.values(onboardingWithoutArchived)
    .filter((t) => t.published_date)
    .map((t) => {
      return {
        value: t.template_id,
        label: t.name,
        plant_touching: t.plant_touching
      };
    })
    .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));

  const relationshipManagerOptions = getRelationshipManagerOptions();

  return (
    <Dialog
      isOpen={isOpen}
      title="Invite Lead to Apply"
      handleClose={onClose}
      action={
        <Button
          label="Send Application"
          color="primary"
          onClick={form.handleSubmit(async (data) => {
            try {
              setIsConverting(true);
              const [{ dispensary }] = await Promise.all([
                convertBankMarketplaceLead({
                  id: lead.id,
                  template_id: data.template_id
                }),
                relationshipManagerStore.saveRelationshipManager(lead.crb_id, data.relationshipManager)
              ]);
              navigate(`/secure/fi/accounts/${dispensary.id}/review`);
            } catch {
              getSnackbarStore().showErrorSnackbarMessage(
                'Something went wrong inviting this business. Contact support for additional help'
              );
            } finally {
              setIsConverting(false);
            }
          })}
          isLoading={isConverting}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Text
          content="Sending this invite will prompt the business to start a full application and move this lead into your pipeline in the Invited queue."
          type="body3"
        />
        <InputSelect
          name="template_id"
          label="Due Diligence Template"
          options={templateOptions}
          addTag="Plant Touching"
          rules={{ required: { message: 'is required', value: true } }}
          {...form}
        />
        <InputSelect
          name="relationshipManager"
          label="Relationship Manager"
          options={relationshipManagerOptions}
          defaultValue={'none'}
          {...form}
        />
      </Box>
    </Dialog>
  );
});
